import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { env } from '@/config/env'
import common from './locales/en-ZA/common.json'
import apply from './locales/en-ZA/apply.json'
import track from './locales/en-ZA/track.json'
import errors from './locales/en-ZA/errors.json'

/**
 * One locale ships today; the plumbing is here from the start.
 *
 * South Africa has twelve official languages and a DHA-branded citizen service
 * will be asked for at least isiZulu and Afrikaans. Externalising strings now is a
 * couple of hours' work; retrofitting it across eight screens of legal and
 * declaration copy is days. What we deliberately have NOT built yet: a language
 * switcher, additional locales, or a translation-management pipeline.
 *
 * Resources are imported statically rather than lazily so that tests assert on
 * real sentences — `getByRole('button', { name: 'Continue' })` — instead of key
 * soup, and so a missing key fails a test rather than rendering the key itself.
 */
export const defaultNS = 'common'

export const resources = {
  'en-ZA': { common, apply, track, errors },
} as const

await i18n.use(initReactI18next).init({
  resources,
  lng: 'en-ZA',
  fallbackLng: 'en-ZA',
  defaultNS,
  ns: ['common', 'apply', 'track', 'errors'],
  /**
   * Correct for React, and deliberately left alone.
   *
   * i18next escapes interpolated values for templating engines that write raw HTML.
   * React already escapes everything it renders, so turning this on would escape
   * twice and a citizen would SEE the entities — "Signed in as O&#39;Brien" for an
   * officer whose surname has an apostrophe.
   *
   * What actually keeps interpolation safe is that no translated string reaches a
   * raw-HTML sink: there is no <Trans>, no dangerouslySetInnerHTML and no innerHTML
   * anywhere in src/, and the ESLint rule in eslint.config.js keeps it that way. If
   * that ever changes, this line is the second thing to revisit — but per-key, not
   * globally.
   */
  interpolation: { escapeValue: false },
  returnNull: false,
  // A key that does not resolve is a defect, not a cosmetic issue. Outside
  // production it stops the test that surfaced it.
  saveMissing: false,
  missingKeyHandler: (_lngs, ns, key) => {
    if (env.appEnv !== 'prod') {
      throw new Error(`Missing translation key: ${ns}:${key}`)
    }
  },
})

export default i18n
